import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import { addAppliedFilters, updateUriRef } from '../../actions/filterActions';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions';
import get from 'lodash.get';
import { FindingRouter } from '../../router/FindingRouter';
import { searchBrowse as routes } from '../../router/routes/searchBrowse';
import { stripRoutingSegment, addCdnQueryParams } from '../../utils/uriUtils';
import {
    toggleRequestInFlight,
    toggleHashFragmentResolved,
    batchAddAndApplyFilter,
    updateZipCode,
    setSemanticSearchTerm,
} from '../../actions/filterActions';
import { getFollowTypeFromUri } from '../../utils/favoritesUtils';
import {
    hasPersonalizedFilterQueryParam,
    getPersonalizedFilterValues,
    getUrlWithoutPersonalizedFilterParam,
    getPersonalizedFilterAction,
    shouldApplyPersonalizedFilter,
    getUserZipCode,
} from '../../shared/helpers/personalizedFiltersHelpers';
import { LoadingComponent } from '../../components/global/LoadingComponent';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';
import {
    getOriginalQueryHashParamValue,
    removeOriginalQueryHashParamValue,
} from '../../utils/hashFragmentHelpers';
const { SEARCH } = pageTypes;

export class SbSharedRefetchContainerComponent extends React.Component {
    constructor() {
        super();
        this.personalizedFilterValues = [];
        this.refetchCallback = this.refetchCallback.bind(this);
    }

    componentDidMount() {
        const { pathname, search, hash } = window.location;
        const { itemSearch = {} } = this.props;
        const { vanityUriRef, searchCorrections = {} } = itemSearch;
        const oqParam = getOriginalQueryHashParamValue(hash);
        const semanticSearchTerm = searchCorrections.semanticSearchTerm || oqParam;

        if (this.isHashFragmentSearchPage()) {
            if (hash) {
                this.props.updateUriRef(`${pathname}${search}${hash}`);
            } else {
                this.props.toggleHashFragmentResolved(true);
            }
        } else {
            // set applied filters in reducer :
            const appliedFilters = itemSearch.appliedFilters;
            this.props.addAppliedFilters(appliedFilters);
        }

        this.initRouter();

        this.props.setSemanticSearchTerm(semanticSearchTerm);

        if (vanityUriRef) {
            // for force facet  or semantic or spell corrected search pages, graphql returned an updated uri,
            // update it in the browser
            this.router.navigate(vanityUriRef, {
                replace: true,
                semanticSearchTerm,
            });
        }

        this.personalizedFilterValues = getPersonalizedFilterValues();

        this.props.updateZipCode(getUserZipCode());
    }

    componentDidUpdate(prevProps) {
        const {
            regionalInfo,
            generatedUriRef,
            page: newPage,
            onSbRefetch,
            pageSize: newPageSize,
            shippingFilters,
        } = this.props;
        /**
         * Make sure we have query params in both uri refs before comparing them to see if they've
         * changed.
         */
        const oldUriRef = addCdnQueryParams(prevProps.generatedUriRef);
        const newUriRef = addCdnQueryParams(generatedUriRef);

        if (
            ((oldUriRef !== '' || this.isHashFragmentSearchPage()) && newUriRef !== oldUriRef) ||
            newPageSize !== prevProps.pageSize
        ) {
            const variables = {
                // if uriRef changed because filter added/removed, reset to page 1
                page: newPage === prevProps.page ? 1 : newPage,
                uriRef: newUriRef,
                followSearchPages: [generatedUriRef],
                followSearchTypes: [getFollowTypeFromUri(generatedUriRef)],
                first: newPageSize,
                disableForceFacet: true,
            };

            // 1. set state to loading
            this.props.toggleRequestInFlight(true);

            // 2. do refetch
            onSbRefetch(variables, null, this.refetchCallback);

            // 3. update to new relay vars
            this.props.updateRelayVariables(variables);
        }

        const regionsByZipCode = get(regionalInfo, '[0].regionsByZipCode');
        if (this.personalizedFilterValues.length && regionsByZipCode) {
            // bug in our current version of eslint, false positive for no-unused-vars in for loop
            // eslint-disable-next-line
            for (const personalizedFilterValue of this.personalizedFilterValues) {
                if (shouldApplyPersonalizedFilter({ personalizedFilterValue, shippingFilters })) {
                    this.props.batchAddAndApplyFilter(
                        getPersonalizedFilterAction(personalizedFilterValue),
                        regionsByZipCode
                    );
                }
            }
        } else if (hasPersonalizedFilterQueryParam()) {
            const urlWithoutPersonalizedFilter = getUrlWithoutPersonalizedFilterParam();
            this.router.navigate(urlWithoutPersonalizedFilter);
        }
    }

    isHashFragmentSearchPage() {
        const isSearch = this.props.itemSearch.pageType === SEARCH;
        return (
            isSearch &&
            !this.context.isSearchQueryApplied &&
            get(this.props, 'viewer.enableSearchLandingPage')
        );
    }

    refetchCallback() {
        const { semanticSearchTerm } = this.props;
        const displayUriRef = get(this.props, 'itemSearch.displayUriRef');
        const newAppliedFilters = get(this.props, 'itemSearch.appliedFilters');

        this.props.toggleRequestInFlight(false);

        this.props.addAppliedFilters(newAppliedFilters);

        if (this.isHashFragmentSearchPage() && !this.props.isHashFragmentResolved) {
            this.props.toggleHashFragmentResolved(true);
        }

        if (displayUriRef) {
            this.router.navigate(displayUriRef, {
                semanticSearchTerm,
            });
        }
    }

    initRouter() {
        this.router = new FindingRouter({ routes });
        this.router.history.listen(({ pathname, search, hash }, action) => {
            if (action === 'POP') {
                const newHash = removeOriginalQueryHashParamValue(hash);
                const uriRef = `${pathname}${search}${newHash}`;
                this.props.updateUriRef(stripRoutingSegment(uriRef));
            }
        });
    }

    render() {
        const { refetchInFlight, isHashFragmentResolved } = this.props;

        if ((this.isHashFragmentSearchPage() && !isHashFragmentResolved) || refetchInFlight) {
            return <LoadingComponent />;
        } else {
            return null;
        }
    }
}

SbSharedRefetchContainerComponent.contextType = ServerVarsContext;

SbSharedRefetchContainerComponent.defaultProps = {
    setSemanticSearchTerm: () => {},
};

SbSharedRefetchContainerComponent.propTypes = {
    updateUriRef: PropTypes.func.isRequired,
    addAppliedFilters: PropTypes.func.isRequired,
    onSbRefetch: PropTypes.func.isRequired,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    // TODO INFRA: Should be required
    generatedUriRef: PropTypes.string,
    toggleRequestInFlight: PropTypes.func.isRequired,
    updateRelayVariables: PropTypes.func.isRequired,
    regionalInfo: PropTypes.array,
    batchAddAndApplyFilter: PropTypes.func.isRequired,
    updateZipCode: PropTypes.func.isRequired,
    shippingFilters: PropTypes.array,
    refetchInFlight: PropTypes.bool.isRequired,
    toggleHashFragmentResolved: PropTypes.func.isRequired,
    isHashFragmentResolved: PropTypes.bool.isRequired,
    setSemanticSearchTerm: PropTypes.func.isRequired,
    semanticSearchTerm: PropTypes.string,
};

function mapStateToProps(state) {
    const { filters, relayVariables } = state;
    const { generatedUriRef, page } = filters;
    const {
        variables: { first },
    } = relayVariables;

    return {
        page,
        generatedUriRef,
        pageSize: first,
        refetchInFlight: filters.refetchInFlight,
        isHashFragmentResolved: filters.isHashFragmentResolved,
        semanticSearchTerm: filters.semanticSearchTerm,
    };
}

export const SbSharedRefetchContainer = createFragmentContainer(
    connect(
        mapStateToProps,
        {
            addAppliedFilters,
            updateUriRef,
            updateRelayVariables,
            toggleRequestInFlight,
            toggleHashFragmentResolved,
            batchAddAndApplyFilter,
            updateZipCode,
            setSemanticSearchTerm,
        }
    )(SbSharedRefetchContainerComponent),
    {
        viewer: graphql`
            fragment SbSharedRefetchContainer_viewer on Viewer {
                enableSearchLandingPage: isEnabled(key: "enableSearchLandingPage")
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefetchContainer_itemSearch on ItemSearchQueryConnection {
                pageType
                vanityUriRef
                displayUriRef
                searchCorrections {
                    semanticSearchTerm
                }
                appliedFilters {
                    name
                    rank
                    values {
                        displayName
                        urlLabel
                        code
                    }
                }
            }
        `,
        regionalInfo: graphql`
            fragment SbSharedRefetchContainer_regionalInfo on RegionalInfo @relay(plural: true) {
                regionsByZipCode {
                    displayName
                    urlLabel
                }
            }
        `,
        shippingFilters: graphql`
            fragment SbSharedRefetchContainer_shippingFilters on ShippingFilterType
                @relay(plural: true) {
                ...personalizedFiltersHelpers_shippingFilters @relay(mask: false)
            }
        `,
    }
);
